<template>
  <ion-header>
    <ion-toolbar>
      <ion-buttons slot="start">
        <ion-button @click="$emit('close')">Cancel</ion-button>
      </ion-buttons>
      <ion-title>{{ entry ? "Edit Field" : "Add Field" }}</ion-title>
      <ion-buttons slot="end">
        <ion-button :disabled="!canAdd || saving" strong @click="submit">
          {{ saving ? "Saving…" : (entry ? "Save" : "Add") }}
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content class="ion-padding">
    <p v-if="errorMessage" class="asem-error">{{ errorMessage }}</p>
    <ion-list>
      <ion-item>
        <ion-label>Type</ion-label>
        <ion-select v-model="entryType" interface="popover" :disabled="!!entry">
          <ion-select-option value="field">Field</ion-select-option>
          <ion-select-option value="schema_ref">Sub-schema</ion-select-option>
        </ion-select>
      </ion-item>

      <template v-if="entryType === 'field'">
        <ion-item>
          <ion-label>Field type</ion-label>
          <ion-select v-model="fieldType" interface="popover">
            <ion-select-option v-for="ft in FIELD_TYPES" :key="ft.value" :value="ft.value">{{ ft.label }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Field name</ion-label>
          <ion-input v-model="name" placeholder="e.g. Last serviced" />
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Internal field name</ion-label>
          <ion-input v-model="internalName" placeholder="e.g. last_serviced" @ionInput="onInternalNameEdited" />
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Description</ion-label>
          <ion-textarea v-model="description" placeholder="Optional description shown to users" :rows="2" />
        </ion-item>
        <DateTimeConfigInput v-if="fieldType === 'datetime'" v-model="dateTimeConfig" />
        <CurrencyConfigInput v-if="fieldType === 'currency'" v-model="currencyConfig" />
        <ion-item>
          <ion-label>Nullable</ion-label>
          <ion-toggle v-model="isNullable" />
        </ion-item>
        <ion-item>
          <ion-label>Multi-value</ion-label>
          <ion-toggle v-model="isMulti" />
        </ion-item>
      </template>
      <template v-else>
        <ion-item>
          <ion-label>Sub-schema</ion-label>
          <ion-select v-model="subSchemaId" interface="popover" :disabled="!!entry">
            <ion-select-option
              v-for="s in availableSubSchemas"
              :key="s.id"
              :value="s.id"
            >{{ s.name }}</ion-select-option>
          </ion-select>
        </ion-item>
      </template>
    </ion-list>
  </ion-content>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import {
  IonButton, IonButtons, IonContent, IonHeader, IonInput, IonItem,
  IonLabel, IonList, IonSelect, IonSelectOption, IonTextarea, IonTitle, IonToggle, IonToolbar,
  alertController,
} from "@ionic/vue";
import type { FieldSchemaOut, SchemaEntryOut, SchemaEntryType, SchemaFieldEntryCreate } from "@/models/field-schemas";
import type { CustomFieldType, DateTimeConfig } from "@/models/custom-fields";
import DateTimeConfigInput from "@/components/DateTimeConfigInput.vue";
import CurrencyConfigInput from "@/components/CurrencyConfigInput.vue";
import { defaultCurrencyConfig, resolveCurrencyConfig, toCurrencyConfig, type ResolvedCurrencyConfig } from "@/helper/currency";
import { defaultDateTimeConfig, resolveDateTimeConfig, toDateTimeTypeConfig } from "@/helper/datetime";
import { useApiStore } from "@/stores/api";
import { useDraft } from "@/composables/useDraft";
import type { Draft } from "@/helper/drafts";

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "String" },
  { value: "textarea", label: "Text" },
  { value: "datetime", label: "Date / time" },
  { value: "boolean", label: "Boolean" },
  { value: "integer", label: "Integer" },
  { value: "decimal", label: "Decimal" },
  { value: "currency", label: "Currency" },
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
  { value: "password", label: "Password" },
  { value: "object_ref", label: "Object reference" },
  { value: "image_ref", label: "Image reference" },
  { value: "barcode_ref", label: "Barcode reference" },
];

const props = defineProps<{
  availableSubSchemas: FieldSchemaOut[];
  /** When set, the modal edits this existing entry instead of drafting a new one. */
  entry?: SchemaEntryOut | null;
  /** Required when `entry` is set — the schema the entry belongs to. */
  schemaId?: number | null;
  /** A saved draft of this dialog to continue, see `askToContinueDraft`. Read once, when the dialog opens. */
  initialDraft?: Draft | null;
}>();

const emit = defineEmits<{
  save: [entry: SchemaFieldEntryCreate];
  saved: [];
  close: [];
}>();

const api = useApiStore();
const saving = ref(false);
const errorMessage = ref<string | null>(null);

const entryType = ref<SchemaEntryType>(props.entry?.entry_type ?? "field");
const name = ref(props.entry?.name ?? "");
const internalName = ref(props.entry?.internal_name ?? "");
const description = ref(props.entry?.description ?? "");
const fieldType = ref<CustomFieldType>(props.entry?.field_type ?? "text");
const isMulti = ref(props.entry?.is_multi ?? false);
const isNullable = ref(props.entry?.is_nullable ?? true);
const dateTimeConfig = ref<DateTimeConfig>(
  props.entry?.field_type === "datetime"
    ? resolveDateTimeConfig(props.entry.type_config)
    : defaultDateTimeConfig(),
);
const currencyConfig = ref<ResolvedCurrencyConfig>(
  props.entry?.field_type === "currency"
    ? resolveCurrencyConfig(props.entry.type_config)
    : defaultCurrencyConfig(),
);
const subSchemaId = ref<number | null>(props.entry?.sub_schema_id ?? null);

function slugifyInternalName(value: string): string {
  const slug = value.trim().toLowerCase().replace(/\W+/g, "_").replace(/^_+|_+$/g, "");
  return slug || "field";
}

// Tracks the last value we computed automatically, so we can tell whether the user has
// since hand-edited the internal name (and stop auto-following the display name if so).
let lastAutoInternalName = props.entry ? "__manually_edited__" : "";

function onInternalNameEdited() {
  lastAutoInternalName = "__manually_edited__";
}

watch(name, (newName) => {
  if (internalName.value !== lastAutoInternalName) return;
  lastAutoInternalName = newName.trim() ? slugifyInternalName(newName) : "";
  internalName.value = lastAutoInternalName;
});

const canAdd = computed(() => {
  if (entryType.value === "field") return !!name.value && !!fieldType.value;
  return !!subSchemaId.value;
});

function draftPayload() {
  return {
    name: entryType.value === "field" ? name.value : null,
    internal_name: entryType.value === "field" ? internalName.value : null,
    description: entryType.value === "field" ? (description.value || null) : null,
    field_type: entryType.value === "field" ? fieldType.value : null,
    // Other types keep their settings (e.g. the password mode) when the key is left out.
    ...(entryType.value === "field" && fieldType.value === "datetime" ? { type_config: toDateTimeTypeConfig(dateTimeConfig.value) } : {}),
    ...(entryType.value === "field" && fieldType.value === "currency" ? { type_config: toCurrencyConfig(currencyConfig.value) } : {}),
    is_multi: isMulti.value,
    is_nullable: isNullable.value,
    sub_schema_id: entryType.value === "schema_ref" ? subSchemaId.value : null,
  };
}

// The draft holds the settings of the entry; the dialog is for a new entry or an entry of a saved schema.
const draftState = useDraft({
  kind: "schema-entry",
  scope: () => `${props.schemaId ?? "new"}:${props.entry?.id ?? "new"}`,
  label: () => name.value.trim() || (props.entry ? `Entry #${props.entry.id}` : "New entry"),
  snapshot: () => ({
    entryType: entryType.value,
    name: name.value,
    internalName: internalName.value,
    description: description.value,
    fieldType: fieldType.value,
    isMulti: isMulti.value,
    isNullable: isNullable.value,
    dateTimeConfig: dateTimeConfig.value,
    currencyConfig: currencyConfig.value,
    subSchemaId: subSchemaId.value,
  }),
});
let finished = false;

function applyInitialDraft() {
  const saved = props.initialDraft?.data;
  if (!saved) return;
  entryType.value = (saved.entryType as SchemaEntryType) ?? entryType.value;
  name.value = (saved.name as string) ?? name.value;
  fieldType.value = (saved.fieldType as CustomFieldType) ?? fieldType.value;
  isMulti.value = (saved.isMulti as boolean) ?? isMulti.value;
  isNullable.value = (saved.isNullable as boolean) ?? isNullable.value;
  dateTimeConfig.value = (saved.dateTimeConfig as DateTimeConfig) ?? dateTimeConfig.value;
  currencyConfig.value = (saved.currencyConfig as ResolvedCurrencyConfig) ?? currencyConfig.value;
  subSchemaId.value = (saved.subSchemaId as number | null) ?? subSchemaId.value;
  description.value = (saved.description as string) ?? description.value;
  // The name watcher above has not run yet, so the restored internal name is set after it: it follows the name only while untouched.
  const savedInternalName = (saved.internalName as string) ?? "";
  lastAutoInternalName = savedInternalName === (name.value.trim() ? slugifyInternalName(name.value) : "")
    ? savedInternalName
    : "__manually_edited__";
  internalName.value = savedInternalName;
}
applyInitialDraft();

onBeforeUnmount(() => {
  if (!finished) draftState.cancel();
});

function submit() {
  if (!canAdd.value) return;
  if (props.entry) {
    saveEdit(false);
  } else {
    finished = true;
    draftState.clear();
    emit("save", { entry_type: entryType.value, ...draftPayload() });
  }
}

async function saveEdit(confirmDataLoss: boolean) {
  if (!props.entry || !props.schemaId) return;
  saving.value = true;
  errorMessage.value = null;
  try {
    await api.updateSchemaEntry({
      schemaId: props.schemaId,
      entryId: props.entry.id,
      patch: { ...draftPayload(), confirm_data_loss: confirmDataLoss ? true : null },
    });
    finished = true;
    draftState.clear();
    emit("saved");
  } catch (e: any) {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 409 && detail?.values_will_be_deleted !== undefined) {
      await confirmDataLossAndRetry(detail.values_will_be_deleted);
    } else {
      errorMessage.value = detail?.message ?? detail ?? e?.message ?? "Failed to save field.";
    }
  } finally {
    saving.value = false;
  }
}

async function confirmDataLossAndRetry(count: number) {
  const alert = await alertController.create({
    header: "Existing values will be deleted",
    message: `There's no automatic conversion between these field types. `
      + `${count} existing value${count === 1 ? "" : "s"} for this field will be permanently deleted. Continue?`,
    buttons: [
      { text: "Cancel", role: "cancel" },
      {
        text: "Delete and change type",
        role: "destructive",
        handler: () => { saveEdit(true); },
      },
    ],
  });
  await alert.present();
}
</script>

<style scoped>
.asem-error {
  color: var(--ion-color-danger);
  font-size: 0.85em;
}
</style>
